// Find anything by name (B-402, PLAN-2026-10-17 §6, DIRECTION U24): a
// player, a team, a league, a screen or a setting, from anywhere, by `/`,
// Ctrl+K or ⌘K on a keyboard, the top bar's search from a tablet up, and the
// account menu's Search on a phone. With fewer pages to click through, it is
// the way to anything whose place a person has forgotten.
//
// IT REPLACES *FIND A PLAYER* (the audit of 2026-10-17), which found players
// only: a name still opens his card, over the screen it was asked from.
//
// PLAYERS COME BACK IN THE POOL'S OWN ORDER, by their average, never ranked
// as advice (DIRECTION U3), and only once two letters are typed; a screen, a
// team or a league is found by every word typed being in its name.
//
// WHAT IS ASKED OF THE SERVER IS ASKED ONLY WHILE IT IS OPEN: the players for
// the words typed, and the league's teams.

import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { failureOf, valueOf, type Availability } from '@fsp/domain';
import { useHub, useLeagueBase, usePlayers, useStandings } from '../data/hooks';
import { useSession } from '../data/session';
import { useLeagueSource } from '../data/leagueSource';
import { useLeagueEntry } from '../data/leagueEntry';
import { leagueIdFromPath } from '../data/route';
import { DEMO_PATH } from '../data/demo';
import { isInstalled } from '../install/platform';
import { PositionPill, TeamMark } from '../kit';
import { Finder, useFinderOpen, type FinderGroup, type FinderItem } from '../kit/finder';
import { useSettled } from '../kit/viewport';
import { openAppearance } from './appearanceSheet';
import { useChatDrawer } from './chatDrawer';
import { leagueDoors } from './leagueScreens';
import { jumping } from './wayBack';
import { clubLabel } from '../sports/registry';

const OPEN_CARD = 'fsp:open-card';

/** Ask the league's card host for a player's card: the finder sits outside it. */
export function askForCard(player: { id: string; name: string }): void {
  window.dispatchEvent(new CustomEvent(OPEN_CARD, { detail: player }));
}

/** The card host's side: open the card a finder asked for. */
export function useCardAsked(open: (player: { id: string; name: string }) => void): void {
  useEffect(() => {
    const on = (event: Event) => open((event as CustomEvent<{ id: string; name: string }>).detail);
    window.addEventListener(OPEN_CARD, on);
    return () => window.removeEventListener(OPEN_CARD, on);
  }, [open]);
}

/** Every word typed is in one of the texts. */
function matches(query: string, ...texts: string[]): boolean {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;
  const hay = texts.join(' ').toLowerCase();
  return words.every((w) => hay.includes(w));
}

/** Who has him, in a word or two. */
function heldBy(at: Availability): string {
  switch (at.kind) {
    case 'mine':
      return 'Your team';
    case 'owned':
      return at.team.name;
    case 'free-agent':
      return 'Free agent';
    case 'waivers':
      return 'On waivers';
  }
}

export function FindAnything({
  leagueMode,
  setup,
  commissioner,
  week,
}: {
  /** Inside a league that has its screens: its players, screens and teams are found. */
  leagueMode: boolean;
  setup: boolean;
  commissioner: boolean;
  /** The week on screen (`?period=N`), which the Scoreboard opens on (B-216); '' for now. */
  week: string;
}) {
  const opened = useFinderOpen();
  const [query, setQuery] = useState('');
  const asked = useSettled(query.trim(), 250);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const base = useLeagueBase();
  const inLeague = leagueMode && leagueIdFromPath(pathname) !== null;
  const real = useLeagueSource() === 'api';
  const { session } = useSession();
  const hub = useHub();
  const entry = useLeagueEntry();
  const chat = useChatDrawer();
  // WHAT WAS FOUND, WITH WHAT IT WAS FOUND FOR: the league and the words. A
  // league left, or words changed since, show nothing of the last answer.
  const [players, setPlayers] = useState<Found>({ key: '', items: [], note: null });
  const [teams, setTeams] = useState<{ base: string; items: FinderItem[] }>({
    base: '',
    items: [],
  });

  const go =
    (to: string, jump = false) =>
    () => {
      if (jump) jumping();
      navigate(to);
    };
  const screen = (
    id: string,
    label: string,
    sub: string,
    icon: FinderItem['icon'],
    onPick: () => void,
  ) => ({ id, label, sub, icon, onPick }) satisfies FinderItem;

  // THE LEAGUE'S PLACES AND SCREENS: the four, then every screen the League
  // page lists (one list, `leagueDoors`), then what can be done.
  const places: FinderItem[] = inLeague
    ? [
        screen('team', 'My Team', 'Your lineup', 'people', go(base, true)),
        screen('gameday', 'Game Day', 'Your matchup', 'matchup', go(`${base}/matchup`, true)),
        screen(
          'players',
          'Players',
          'Every player in the league',
          'search',
          go(`${base}/players`, true),
        ),
        screen('league', 'League', 'The league at a glance', 'leagues', go(`${base}/league`, true)),
        screen(
          'calendar',
          'Calendar',
          'When things happen this week',
          'calendar',
          go(`${base}/calendar`, true),
        ),
        ...leagueDoors(base, setup, commissioner, real).map((d) =>
          d.kind === 'chat'
            ? screen('chat', d.label, 'League chat', d.icon, () => chat.setOpen(true))
            : screen(
                d.to,
                d.label,
                'League',
                d.icon,
                // The Scoreboard opens on the week on screen, as the rail's did.
                go(d.to === `${base}/scoreboard` ? `${d.to}${week}` : d.to),
              ),
        ),
        ...(setup
          ? []
          : [screen('propose', 'Propose a trade', 'Trades', 'trade', go(`${base}/trades?new=1`))]),
      ]
    : [];
  // THE ACCOUNT'S, from anywhere.
  const account: FinderItem[] = [
    screen(
      'appearance',
      'Appearance',
      'Look, colors, text size and detail',
      'looks',
      openAppearance,
    ),
    ...(session
      ? [
          screen('account', 'Account', 'Your name, email and password', 'person', go('/account')),
          screen(
            'notifications',
            'Notifications',
            'What reaches you, and how',
            'notifications',
            go('/account/notifications'),
          ),
          screen(
            'achievements',
            'Achievements',
            'Your wall',
            'achievements',
            go('/account/achievements'),
          ),
          screen('leagues', 'My leagues', 'Every league you are in', 'leagues', go('/', true)),
          screen('new', 'Start a league', 'A new league', 'add', go('/leagues/new')),
          screen('join', 'Join a league', 'With an invite code', 'invite', go('/join')),
          ...(isInstalled()
            ? []
            : [
                screen(
                  'install',
                  'Install the app',
                  'On this device',
                  'install',
                  go('/account/install'),
                ),
              ]),
        ]
      : [screen('demo', 'Demo league', 'Look around', 'play', go(DEMO_PATH))]),
  ];
  // MY LEAGUES, by name: the one on screen is where you are already.
  const here = leagueIdFromPath(pathname);
  const leagues: FinderItem[] = hub.leagues
    .filter((l) => l.league.id !== here)
    .map((l) => ({
      id: `league-${l.league.id}`,
      label: l.league.name,
      sub: 'League',
      lead: l.myTeam ? <TeamMark team={l.myTeam} size={22} /> : undefined,
      icon: 'leagues' as const,
      onPick: go(entry(l.league.id, l.league.status === 'setup'), true),
    }));

  const q = query.trim();
  const playersHere = q.length >= 2 && inLeague;
  // Current only when found in this league for exactly these words.
  const found = playersHere && players.key === `${base}|${q}` ? players : null;
  const looking = playersHere && (found === null || found.note === LOOKING);
  const groups: FinderGroup[] = [
    { label: 'Players', items: found?.items ?? [] },
    {
      label: inLeague ? 'In this league' : 'Go to',
      items: places.filter((i) => matches(q, i.label, i.sub ?? '')),
    },
    {
      label: 'Teams',
      items:
        q.length > 0 && inLeague && !setup && teams.base === base
          ? teams.items.filter((i) => matches(q, i.label, i.sub ?? ''))
          : [],
    },
    { label: 'Leagues', items: leagues.filter((i) => matches(q, i.label)) },
    { label: 'Your account', items: account.filter((i) => matches(q, i.label, i.sub ?? '')) },
  ];
  return (
    <>
      <Finder
        groups={groups}
        query={query}
        onQuery={setQuery}
        placeholder={inLeague ? 'Players, teams, screens' : 'Leagues, screens, settings'}
        // Nothing is said to be missing while players are still being asked for.
        empty={looking ? LOOKING : 'Nothing by that name.'}
        note={looking ? LOOKING : (found?.note ?? null)}
      />
      {opened && inLeague && asked.length >= 2 ? (
        <PlayerMatches q={asked} base={base} onItems={setPlayers} />
      ) : null}
      {opened && inLeague && !setup ? <TeamMatches base={base} onItems={setTeams} /> : null}
    </>
  );
}

const LOOKING = 'Looking for players…';

/** Players found, for which league and which words (`base|words`). */
interface Found {
  key: string;
  items: FinderItem[];
  note: string | null;
}

/** The players for the words typed, from the league's own pool. Draws nothing. */
function PlayerMatches({
  q,
  base,
  onItems,
}: {
  q: string;
  base: string;
  onItems: (found: Found) => void;
}) {
  const read = usePlayers({
    availability: 'all',
    positions: [],
    q,
    sort: 'average',
    reverse: false,
    cursor: null,
    limit: 8,
  });
  const page = valueOf(read.snapshot);
  const failed = page === null ? failureOf(read.snapshot) : null;
  useEffect(() => {
    onItems({
      key: `${base}|${q}`,
      items: (page?.items ?? []).map((r) => ({
        id: `player-${r.player.id}`,
        label: r.player.name,
        sub: `${r.player.proTeam === null ? 'No club' : clubLabel(r.player.proTeam)} · ${heldBy(r.availability)}`,
        lead: r.player.positions[0] ? (
          <PositionPill pos={r.player.positions[0]} size="sm" />
        ) : undefined,
        onPick: () => askForCard({ id: r.player.id, name: r.player.name }),
      })),
      note: failed !== null ? failed.message : page === null ? LOOKING : null,
    });
  }, [page, failed, onItems, base, q]);
  return null;
}

/** The league's teams, each to its page. Draws nothing. */
function TeamMatches({
  base,
  onItems,
}: {
  base: string;
  onItems: (found: { base: string; items: FinderItem[] }) => void;
}) {
  const table = valueOf(useStandings());
  const navigate = useNavigate();
  useEffect(() => {
    onItems({
      base,
      items: (table?.rows ?? []).map((r) => ({
        id: `team-${r.team.id}`,
        label: r.team.name,
        sub: r.team.isMine ? 'Your team' : 'Team',
        lead: <TeamMark team={r.team} size={22} />,
        // A link like any other: Back returns to the screen it was found on.
        onPick: () => navigate(`${base}/teams/${r.team.id}`),
      })),
    });
  }, [table, base, onItems, navigate]);
  return null;
}
